import { useMemo, useState } from 'react';
import {
  FieldExtensionComponentProps,
  makeFieldSchema,
} from '@backstage/plugin-scaffolder-react';
import {
  FormFieldBlueprint,
  createFormField,
} from '@backstage/plugin-scaffolder-react/alpha';
import {
  Box,
  ButtonIcon,
  ButtonLink,
  Flex,
  Text,
  TextField,
} from '@backstage/ui';
import { RiFileCopyLine, RiExternalLinkLine } from '@remixicon/react';
import type { ExtensionDefinition } from '@backstage/frontend-plugin-api';
import styles from './GoogleClientIdField.module.css';

type GoogleClientIdUiOptions = {
  consoleUrl?: string;
};

type GoogleClientIdFieldProps = FieldExtensionComponentProps<
  string,
  GoogleClientIdUiOptions
>;

const defaultConsoleUrl =
  'https://console.cloud.google.com/auth/clients?hl=en&project=kansotech';
const fingerprint =
  '36:7C:F8:C0:D1:67:B7:50:67:35:09:B8:BC:CB:D7:3C:01:22:4C:C4';

type SuggestionProps = {
  label: string;
  value: string;
  copyKey: string;
  copied?: string;
  onCopy: (key: string, value: string) => Promise<void>;
};

function Suggestion({
  label,
  value,
  copyKey,
  copied,
  onCopy,
}: SuggestionProps) {
  return (
    <Flex align="center" style={{ gap: 'var(--bui-space-2)' }}>
      <TextField
        className={styles.field}
        label={label}
        value={value}
        isReadOnly
      />
      <ButtonIcon
        aria-label={copied === copyKey ? `${label} copied` : `Copy ${label}`}
        onPress={() => void onCopy(copyKey, value)}
        icon={<RiFileCopyLine size={20} />}
        variant="secondary"
      />
    </Flex>
  );
}

const GoogleClientIdField = ({
  onChange,
  rawErrors,
  required,
  schema,
  uiSchema,
  formData: fieldValue,
  formContext,
}: GoogleClientIdFieldProps) => {
  const [copied, setCopied] = useState<string>();
  const formData = (formContext?.formData ?? {}) as { projectName?: string };
  const projectName = formData.projectName ?? '';
  const suggestions = useMemo(
    () => ({
      name: projectName,
      packageName: `com.kansotech.${projectName.replace(/-/g, '_')}`,
      fingerprint,
    }),
    [projectName],
  );
  const options = (uiSchema?.['ui:options'] ?? {}) as GoogleClientIdUiOptions;
  const consoleUrl = options.consoleUrl ?? defaultConsoleUrl;

  const copy = async (key: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    window.setTimeout(() => {
      setCopied((current: string | undefined) =>
        current === key ? undefined : current,
      );
    }, 1500);
  };

  return (
    <Flex direction="column" style={{ gap: 'var(--bui-space-4)' }}>
      <TextField
        className={styles.field}
        label={schema.title}
        value={fieldValue ?? ''}
        onChange={onChange}
        isRequired={required}
        isInvalid={Boolean(rawErrors?.length)}
        description={
          rawErrors?.join(', ') ??
          'Enter the Google OAuth 2.0 Web client ID ending in .apps.googleusercontent.com.'
        }
      />
      <Box>
        <Text variant="title-x-small">Google Cloud client setup</Text>
        <Text variant="body-small" color="secondary">
          Use these values when creating the Android client in Google Cloud.
        </Text>
        <ButtonLink
          href={consoleUrl}
          target="_blank"
          rel="noopener noreferrer"
          iconStart={<RiExternalLinkLine size={18} />}
          style={{ marginTop: 'var(--bui-space-2)' }}
        >
          Open Google Auth Platform
        </ButtonLink>
      </Box>
      <Suggestion
        label="Name"
        value={suggestions.name}
        copyKey="name"
        copied={copied}
        onCopy={copy}
      />
      <Suggestion
        label="Package name"
        value={suggestions.packageName}
        copyKey="packageName"
        copied={copied}
        onCopy={copy}
      />
      <Suggestion
        label="SHA-1 certificate fingerprint"
        value={suggestions.fingerprint}
        copyKey="fingerprint"
        copied={copied}
        onCopy={copy}
      />
    </Flex>
  );
};

const googleClientIdField = createFormField({
  name: 'GoogleClientIdField',
  component: GoogleClientIdField,
  schema: makeFieldSchema({
    output: zImpl => zImpl.string(),
    uiOptions: zImpl =>
      zImpl.object({
        consoleUrl: zImpl.string().optional(),
      }),
  }),
});

export const googleClientIdFieldExtension: ExtensionDefinition =
  FormFieldBlueprint.make({
    name: 'GoogleClientIdField',
    params: {
      field: async () => googleClientIdField,
    },
  });

export default googleClientIdFieldExtension;
